if (!customElements.get('product-kit-customize')) {
  class ProductKitCustomize extends HTMLElement {
    connectedCallback() {
      this._form = document.getElementById(this.dataset.formId);
      this._maxLetters = parseInt(this.dataset.maxLetters || '3');
      this._maxTextarea = parseInt(this.dataset.maxTextarea || '30');
      this._hasOrientation = this.dataset.hasOrientation === 'true';
      this._showNameLaser = this.dataset.showNameLaser === 'true';
      this._showPersonalizacao = this.dataset.showPersonalizacao === 'true';
      this._showNome = this.dataset.showNome === 'true';
      this._showCaixa = this.dataset.showCaixa === 'true';

      this._bindAll();
      this._update();

      if (this._form) {
        // Capture phase garante que injetamos antes do handler do product-form.js (bubble)
        this._form.addEventListener('submit', this._onSubmit.bind(this), true);
      }
    }

    // --- element getters (query within component scope, no global IDs needed) ---

    get _initialsInput()   { return this.querySelector('[data-field="initials"]'); }
    get _colorSelect()     { return this.querySelector('[data-field="letters-color"]'); }
    get _nameArea()        { return this.querySelector('[data-field="name-area"]'); }
    get _personalizedCb()  { return this.querySelector('[data-field="personalized-name"]'); }
    get _withoutCb()       { return this.querySelector('[data-field="without-personalization"]'); }
    get _blankCardCb()     { return this.querySelector('[data-field="card-blank"]'); }
    get _cardTextarea()    { return this.querySelector('[data-field="card-text"]'); }
    get _counter()         { return this.querySelector('[data-counter]'); }
    get _nameTextSection() { return this.querySelector('[data-section="name-text"]'); }
    get _lettersSection()  { return this.querySelector('[data-section="letters"]'); }
    get _submitBtn()       { return this._form && this._form.querySelector('[name="add"]'); }

    // --- event binding ---

    _bindAll() {
      const sanitize = v => v.replace(/[^a-zA-Z0-9& ]/gm, '');

      if (this._initialsInput) {
        this._initialsInput.addEventListener('input', () => {
          this._initialsInput.value = sanitize(this._initialsInput.value);
          this._update();
        });
        this._initialsInput.addEventListener('paste', () =>
          setTimeout(() => { this._initialsInput.value = sanitize(this._initialsInput.value); this._update(); }, 0)
        );
      }

      if (this._colorSelect) {
        this._colorSelect.addEventListener('input', () => this._update());
      }

      if (this._nameArea) {
        this._nameArea.addEventListener('input', () => {
          const max = this._maxTextarea;
          if (this._nameArea.value.length > max) {
            const cursor = this._nameArea.selectionStart;
            this._nameArea.value = this._nameArea.value.slice(0, max);
            this._nameArea.selectionStart = this._nameArea.selectionEnd = cursor - 1;
          }
          this._update();
        });
      }

      this.querySelectorAll('[data-field="font"]').forEach(r =>
        r.addEventListener('click', () => this._update())
      );

      this.querySelectorAll('[data-field="orientation"]').forEach(r =>
        r.addEventListener('click', () => this._update())
      );

      if (this._personalizedCb) {
        this._personalizedCb.addEventListener('change', () => {
          if (this._withoutCb) this._withoutCb.checked = false;
          this._toggleNameLaserDivs();
          this._update();
        });
      }

      if (this._withoutCb) {
        this._withoutCb.addEventListener('change', () => {
          if (this._withoutCb.checked && this._personalizedCb) {
            this._personalizedCb.checked = false;
            this._toggleNameLaserDivs();
          }
          this._update();
        });
      }

      if (this._cardTextarea) {
        this._cardTextarea.addEventListener('input', () => {
          if (this._cardTextarea.value.length > 300) {
            const cursor = this._cardTextarea.selectionStart;
            this._cardTextarea.value = this._cardTextarea.value.slice(0, 300);
            this._cardTextarea.selectionStart = this._cardTextarea.selectionEnd = cursor - 1;
          }
          if (this._counter) this._counter.textContent = `${this._cardTextarea.value.length}/300`;
          this._update();
        });
      }

      if (this._blankCardCb) {
        this._blankCardCb.addEventListener('change', () => {
          const target = this._cardTextarea || this._nameArea;
          if (target) {
            target.disabled = this._blankCardCb.checked;
            if (this._blankCardCb.checked) target.value = '';
          }
          this._update();
        });
      }
    }

    // --- show/hide name-laser vs initials sections ---

    _toggleNameLaserDivs() {
      const isLaser = this._personalizedCb && this._personalizedCb.checked;

      if (this._nameArea)       this._nameArea.value = '';
      if (this._initialsInput)  this._initialsInput.value = '';
      if (this._colorSelect)    this._colorSelect.selectedIndex = 0;
      this.querySelectorAll('[data-field="font"]').forEach(r => (r.checked = false));

      if (this._nameTextSection) this._nameTextSection.classList.toggle('hidden', !isLaser);
      if (this._lettersSection)  this._lettersSection.classList.toggle('hidden', isLaser);
    }

    // --- completeness checks ---

    _isPersonalizationComplete() {
      // No personalization section at all — trivially complete
      if (!this._showNameLaser && !this._showPersonalizacao && !this._showNome) return true;

      if (this._withoutCb && this._withoutCb.checked) return true;

      if (this._showNameLaser) {
        if (this._personalizedCb && this._personalizedCb.checked) {
          const nameOk = this._nameArea && this._nameArea.value.length >= 1;
          const fontOk = !!this.querySelector('[data-field="font"]:checked');
          return nameOk && fontOk;
        }
        // Default: initials flow
        const initialsOk  = this._initialsInput && this._initialsInput.value.length >= 1;
        const colorOk     = this._colorSelect && this._colorSelect.value !== '';
        const orientOk    = !this._hasOrientation || !!this.querySelector('[data-field="orientation"]:checked');
        return initialsOk && colorOk && orientOk;
      }

      if (this._showPersonalizacao) {
        const initialsOk = this._initialsInput && this._initialsInput.value.length >= 1;
        const colorOk    = this._colorSelect && this._colorSelect.value !== '';
        const orientOk   = !this._hasOrientation || !!this.querySelector('[data-field="orientation"]:checked');
        return initialsOk && colorOk && orientOk;
      }

      if (this._showNome) {
        if (this._blankCardCb && this._blankCardCb.checked) return true;
        return this._nameArea && this._nameArea.value.length >= 1;
      }

      return true;
    }

    _isCaixaComplete() {
      if (!this._showCaixa) return true;
      if (this._blankCardCb && this._blankCardCb.checked) return true;
      return this._cardTextarea && this._cardTextarea.value.length >= 1;
    }

    // Atualiza botão E sincroniza as hidden inputs — chamado em toda mudança de campo
    _update() {
      const complete = this._isPersonalizationComplete() && this._isCaixaComplete();
      const btn = this._submitBtn;
      if (btn) btn.disabled = !complete;
      this._syncProperties();
    }

    _updateSubmitButton() {
      const btn = this._submitBtn;
      if (!btn) return;
      btn.disabled = !this._isPersonalizationComplete() || !this._isCaixaComplete();
    }

    // Mantém as hidden inputs no form em sincronia com o estado atual dos campos.
    // Assim o FormData do product-form.js captura tudo corretamente na hora do submit.
    _syncProperties() {
      this._injectProperties(this._collectProperties());
    }

    // --- property collection & injection ---

    _collectProperties() {
      const props = {};

      if (this._withoutCb && this._withoutCb.checked) {
        props['Sem personalização manual'] = 'Sim';
        return props;
      }

      if (this._blankCardCb && this._blankCardCb.checked) {
        props['Cartão em branco'] = 'Sim';
        return props;
      }

      if (this._personalizedCb && this._personalizedCb.checked) {
        if (this._nameArea && this._nameArea.value)
          props['Nome personalizado'] = this._nameArea.value;
        const font = this.querySelector('[data-field="font"]:checked');
        if (font) props['Fonte Selecionada'] = font.value;
      } else {
        if (this._initialsInput && this._initialsInput.value)
          props['Iniciais'] = this._initialsInput.value.toUpperCase();
        if (this._colorSelect && this._colorSelect.value)
          props['Cor das Iniciais'] = this._colorSelect.value;
      }

      const orient = this.querySelector('[data-field="orientation"]:checked');
      if (orient) props['Orientação Selecionada'] = orient.value;

      if (this._cardTextarea && this._cardTextarea.value)
        props['Cartão'] = this._cardTextarea.value;

      return props;
    }

    _injectProperties(props) {
      if (!this._form) return;
      this._form.querySelectorAll('[data-kit-prop]').forEach(el => el.remove());
      Object.entries(props).forEach(([key, val]) => {
        if (!val) return;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${key}]`;
        input.value = val;
        input.dataset.kitProp = '';
        this._form.appendChild(input);
      });
    }

    // Capture phase: sync sempre. Se "nome a laser" está marcado, assumimos o controle
    // total do submit para adicionar o produto de personalização junto.
    _onSubmit(event) {
      this._syncProperties();

      if (this._personalizedCb && this._personalizedCb.checked) {
        event.preventDefault();
        event.stopPropagation();
        this._addToCartWithPersonalization();
      }
    }

    async _addToCartWithPersonalization() {
      const variantIdInput = this._form.querySelector('input[name="id"]');
      if (!variantIdInput) return;

      const variantId = parseInt(variantIdInput.value);
      const qtyInput  = this._form.querySelector('input[name="quantity"]');
      const quantity  = qtyInput ? parseInt(qtyInput.value) || 1 : 1;
      const props     = this._collectProperties();

      const btn = this._submitBtn;
      if (btn) { btn.disabled = true; btn.classList.add('loading'); }

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [
              { id: variantId, quantity, properties: props },
              { id: 49739107500343, quantity: 1 }
            ],
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          })
        });

        const data = await response.json();

        if (data.status) {
          console.error('Erro ao adicionar ao carrinho:', data.description || data.status);
        } else {
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && data.sections) {
            cartDrawer.renderContents(data);
          } else {
            window.location.href = window.routes ? window.routes.cart_url : '/cart';
          }
        }
      } catch (err) {
        console.error('Erro de rede ao adicionar ao carrinho:', err);
      } finally {
        if (btn) { btn.disabled = false; btn.classList.remove('loading'); }
        this._update();
      }
    }
  }

  customElements.define('product-kit-customize', ProductKitCustomize);
}
